import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { Heart, Sparkles, MessageCircle, Users, BookOpen, User, LogOut, LayoutDashboard, ShieldCheck, MapPin, ScrollText, Award } from "lucide-react";

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const nItems = [
    { to: "/app", label: "Today", icon: Sparkles, testid: "nav-today" },
    { to: "/app/discover", label: "Discover", icon: Heart, testid: "nav-discover" },
    { to: "/app/messages", label: "Messages", icon: MessageCircle, testid: "nav-messages" },
    { to: "/app/paths", label: "Paths", icon: MapPin, testid: "nav-paths" },
    { to: "/app/ledger", label: "Ledger", icon: ScrollText, testid: "nav-ledger" },
    { to: "/app/weekly", label: "Weekly", icon: Award, testid: "nav-weekly" },
    { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
  ];

  return (
    <div className="min-h-screen">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/app" data-testid="app-logo-link"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {nItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/app"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-full transition-all duration-500 ${
                    isActive ? "bg-navy text-cream-100" : "text-navy hover:bg-white/60"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-navy hover:bg-white/60"
              >
                <ShieldCheck size={16} /> Admin
              </Link>
            )}
            {user?.premium ? (
              <span className="hidden sm:inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose text-white" data-testid="premium-badge">
                <Sparkles size={12} /> Premium
              </span>
            ) : (
              <Link to="/premium" data-testid="nav-premium" className="hidden sm:inline-flex text-xs px-3 py-1.5 rounded-full bg-navy text-cream-100 hover:bg-navy-600 transition-all duration-500">
                Go Premium
              </Link>
            )}
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className="p-2 rounded-full text-navy hover:bg-white/60 transition-all duration-500"
              title="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
        {/* Mobile bottom nav */}
        <nav className="md:hidden flex items-center justify-around px-2 py-2 border-t border-navy/10">
          {nItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === "/app"} data-testid={`m-${it.testid}`}
              className={({ isActive }) => `flex flex-col items-center text-[10px] px-2 py-1 rounded-lg ${isActive ? "text-rose" : "text-navy/70"}`}>
              <it.icon size={18} />
              {it.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>
      <footer className="max-w-7xl mx-auto px-6 py-10 text-xs text-navy/60">
        <div className="flex flex-wrap justify-between gap-4">
          <div>© 2026 Wholesome™ · Real People. Genuine Connections. Meaningful Love.</div>
          <div className="flex gap-4">
            <Link to="/app/date-ideas" data-testid="footer-date-ideas">Date ideas</Link>
            <Link to="/app/learn" data-testid="footer-learn">Learning Centre</Link>
            <Link to="/premium" data-testid="footer-premium">Premium</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AppShell;
